import { initializeApp, getApps } from "firebase/app";
import { getToken, getMessaging, isSupported } from "firebase/messaging";
import * as Sentry from "@sentry/react";

import protectedAxios from "./axios";
import { API_URL } from "./constants";
import { getDeviceInfo } from "./deviceInfo";

const firebaseConfig = {
  apiKey: "AIzaSyBVTya9AiFcncvvy4diYUYRXMqKXEPzui8",
  authDomain: "unified-notifications-f4311.firebaseapp.com",
  projectId: "unified-notifications-f4311",
  storageBucket: "unified-notifications-f4311.appspot.com",
  messagingSenderId: "235608702648",
  appId: "1:235608702648:web:ea27f432ac2bee797fbe4d",
  measurementId: "G-KCF24NQ58M",
};

const VAPID_KEY =
  "BD5REaDi9RqHg5Le2cuidibgCDm1IJ7G6IkSlWUVMsYbLy0TZDgDmHd99WKUopIZDNBbZRvpIoIhiVyDke2ad-s";

const requestNotificationPermission = async () => {
  try {
    if (typeof Notification !== "undefined") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
  } catch (error) {
    console.error("An error occurred while requesting permission", error);
    return false;
  }
};
// Service worker registration with retry logic
const registerServiceWorker = async (retries = 3) => {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser");
    return null;
  }

  for (let i = 0; i < retries; i++) {
    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" }
      );

      // Wait for the service worker to be active
      await navigator.serviceWorker.ready;

      console.info(
        "Service worker registered successfully:",
        registration.scope
      );
      return registration;
    } catch (error) {
      console.warn(
        `Service worker registration attempt ${i + 1} failed:`,
        error
      );

      if (i === retries - 1) {
        throw error;
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
  return null;
};

export async function initializerFirebase(requestPermission = true) {
  try {
    // Check if Firebase messaging is supported in this browser
    const messagingSupported = await isSupported();

    if (!messagingSupported) {
      console.warn("Firebase messaging is not supported in this browser");
      return false;
    }

    // Register service worker first
    try {
      await registerServiceWorker();
    } catch (swError) {
      console.error("Service worker registration failed:", swError);
      // Continue even if service worker fails - some browsers might still work
    }

    if (getApps().length === 0) initializeApp(firebaseConfig);

    if (
      typeof Notification !== "undefined" &&
      (requestPermission || Notification.permission === "granted")
    ) {
      try {
        const permissionGranted = await requestNotificationPermission();

        if (permissionGranted) {
          const message = await getMessaging(getApps()[0]);

          // Get token with retry logic
          let token = null;
          let retries = 3;

          for (let attempt = 0; attempt < retries; attempt++) {
            try {
              token = await getToken(message, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: await navigator.serviceWorker.ready,
              });

              if (token) break;
            } catch (tokenError) {
              console.warn(
                `Token generation attempt ${attempt + 1} failed:`,
                tokenError
              );

              // Check for specific error types
              if (tokenError.message?.includes("push service error")) {
                // Wait longer for push service errors
                await new Promise((resolve) =>
                  setTimeout(resolve, 2000 * (attempt + 1))
                );
              } else if (attempt === retries - 1) {
                throw tokenError;
              } else {
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
            }
          }

          if (!token) {
            throw new Error("Failed to get FCM token after multiple attempts");
          }

          const instanceId = token.split(":")[0];

          if (token && instanceId) {
            try {
              // First check if this device is already registered
              const devicesResponse = await protectedAxios.get(
                `${API_URL}/v1/notification/devices`
              );

              if (
                devicesResponse.data.success &&
                devicesResponse.data.devices
              ) {
                const deviceExists = devicesResponse.data.devices.some(
                  (device) => device.instanceId === instanceId
                );

                // Only register if device doesn't exist
                if (!deviceExists) {
                  const deviceInfo = getDeviceInfo();

                  await protectedAxios.post(
                    `${API_URL}/v1/notification/token-store`,
                    {
                      instanceId,
                      token,
                      deviceInfo,
                    }
                  );
                  console.info("Device registered successfully");
                } else {
                  console.info(
                    "Device already registered, skipping registration"
                  );
                }
              }
            } catch (error) {
              console.error("Error during device registration:", error);
            }
          } else {
            console.error(
              "Something went wrong...please check token && installationId"
            );
          }
        }
      } catch (error) {
        // Don't report to Sentry for expected errors
        const ignoredErrors = [
          "unsupported-browser",
          "messaging/permission-blocked",
          "messaging/unsupported-browser",
          "messaging/failed-service-worker-registration",
        ];

        const shouldReportToSentry = !ignoredErrors.some(
          (ignored) =>
            error.message?.includes(ignored) || error.code?.includes(ignored)
        );

        if (shouldReportToSentry) {
          Sentry.captureException(error, {
            tags: {
              component: "firebase-push-notifications",
              action: "initialization",
            },
            extra: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
            },
          });
        }
        console.error("Firebase initialization error:", error);
      }
    }

    return true;
  } catch (error) {
    // Don't report to Sentry if it's just unsupported browser
    if (!error.message?.includes("unsupported-browser")) {
      Sentry.captureException(error);
    }
    console.error("Firebase initialization error:", error);
    return false;
  }
}

/**
 * Get current FCM token if available (without requesting permission)
 */
export async function getCurrentFCMToken() {
  try {
    // Check if Firebase messaging is supported
    const messagingSupported = await isSupported();
    if (!messagingSupported) {
      return null;
    }

    // Check if permission is granted
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return null;
    }

    // Ensure service worker is registered
    if ("serviceWorker" in navigator) {
      try {
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.warn("Service worker not ready:", swError);
      }
    }

    if (getApps().length === 0) {
      initializeApp(firebaseConfig);
    }

    const messaging = getMessaging(getApps()[0]);

    try {
      const swRegistration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });
      return token;
    } catch (error) {
      // Only log non-critical errors as warnings
      if (error.message?.includes("push service error")) {
        console.warn("Push service temporarily unavailable:", error.message);
      } else {
        console.warn("No FCM token available:", error);
      }
      return null;
    }
  } catch (error) {
    console.error("Error getting current FCM token:", error);
    return null;
  }
}
